import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const milestones = [
  { year: '2018', title: '品牌创立', desc: '蚂蚁影视正式成立，致力于打造免费高清在线观影平台' },
  { year: '2019', title: '用户突破100万', desc: '凭借优质内容和流畅体验，注册用户数突破100万大关' },
  { year: '2020', title: 'APP正式上线', desc: 'iOS和Android双平台APP同步发布，实现多端无缝观影' },
  { year: '2021', title: '内容库突破5万', desc: '影视资源库扩充至5万+，覆盖电影、电视剧、综艺、动漫等全品类' },
  { year: '2022', title: '4K超清上线', desc: '率先推出4K超清画质选项，全面提升用户观影体验' },
  { year: '2023', title: '用户突破1000万', desc: '累计服务用户超过1000万，成为国内领先的免费观影平台' },
  { year: '2024', title: 'AI智能推荐', desc: '引入AI技术实现个性化内容推荐，让用户发现更多喜爱的内容' },
  { year: '2025', title: '全球CDN加速', desc: '在全球范围内部署CDN节点，为海外用户提供同样流畅的观影体验' },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone_alt: '',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.phone_alt.trim()) {
      setFormState('success');
      setFormData({ name: '', email: '', subject: '', message: '', phone_alt: '' });
      setTimeout(() => setFormState('idle'), 3000);
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const form = e.currentTarget as HTMLFormElement;
      const payload = new FormData(form);
      payload.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9ng7ukit35ii3jdlr00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormData({ name: '', email: '', subject: '', message: '', phone_alt: '' });
      } else if (!response.ok || serverMsg?.includes?.('spam') || serverMsg?.includes?.('form data is spam')) {
        setFormState('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      } else {
        setFormState('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setFormState('error');
      setFormError('网络异常，请检查网络连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="site-container">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Introduction */}
        <section className="py-10 md:py-14">
          <div className="site-container">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">
                关于蚂蚁影视
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                蚂蚁影视创立于2018年，是国内领先的免费高清在线观影平台。我们始终秉持"让每个人都能轻松享受影视乐趣"的初心，通过技术创新和内容积累，为数千万用户提供流畅、高品质的观影体验。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto">
              {[
                { icon: 'ri-user-smile-line', value: '2000万+', label: '服务用户' },
                { icon: 'ri-movie-2-line', value: '10万+', label: '影视资源' },
                { icon: 'ri-earth-line', value: '99.9%', label: '服务可用率' },
                { icon: 'ri-thumb-up-line', value: '4.8分', label: '用户评分' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="site-container">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                发展历程
              </h2>
              <p className="text-sm text-foreground-500 mt-1">一路走来的重要时刻</p>
            </div>

            <div className="max-w-2xl mx-auto">
              {milestones.map((item, index) => (
                <div key={item.year} className="flex gap-4 md:gap-6">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                      index === milestones.length - 1 ? 'bg-accent-500' : 'bg-primary-500'
                    }`}></div>
                    {index < milestones.length - 1 && (
                      <div className="w-0.5 flex-1 bg-background-300 my-1"></div>
                    )}
                  </div>
                  <div className={`pb-8 ${index === milestones.length - 1 ? 'pb-0' : ''}`}>
                    <span className="inline-block px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-semibold mb-1.5">
                      {item.year}
                    </span>
                    <h3 className="text-base font-semibold text-foreground-900 font-heading">{item.title}</h3>
                    <p className="text-sm text-foreground-500 mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-10 md:py-14">
          <div className="site-container">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                核心价值观
              </h2>
              <p className="text-sm text-foreground-500 mt-1">驱动我们不断前行的力量</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto">
              {[
                { icon: 'ri-heart-line', title: '用户至上', desc: '一切以用户体验为中心，持续优化产品功能和播放体验，让观影更简单、更舒适。' },
                { icon: 'ri-shield-check-line', title: '品质承诺', desc: '严格筛选内容质量，确保高清画质和流畅播放。我们只提供经过精挑细选的优质内容。' },
                { icon: 'ri-lightbulb-line', title: '创新驱动', desc: '不断探索新技术的应用，从AI推荐到智能编码，用创新为用户创造更多价值。' },
              ].map((value) => (
                <div key={value.title} className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${value.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-10 md:py-14 bg-background-100" id="contact">
          <div className="site-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-4xl mx-auto">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                  有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队会在24小时内回复。
                </p>

                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@mantfilm.com' },
                    { icon: 'ri-phone-line', label: '客服电话', value: '400-888-XXXX' },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: '广东省深圳市南山区科技园路XX号' },
                    { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
                  ].map((info) => (
                    <div key={info.label} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className={`${info.icon} text-primary-600 text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{info.label}</p>
                        <p className="text-sm font-medium text-foreground-800">{info.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-4">发送消息</h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex flex-col gap-3"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1">
                      主题
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm outline-none focus:border-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="">请选择主题</option>
                      <option value="问题反馈">问题反馈</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="内容建议">内容建议</option>
                      <option value="商务合作">商务合作</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1">
                      留言内容
                      <span className="text-foreground-400 ml-1">({formData.message.length}/500)</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入留言内容，最多500字"
                    />
                  </div>

                  {/* Anti-spam honeypot */}
                  <div className="phone-alt-field" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                    <input
                      type="text"
                      name="phone_alt"
                      value={formData.phone_alt}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly={false}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formState === 'submitting' ? '提交中...' : '发送消息'}
                  </button>

                  {formState === 'success' && (
                    <p className="text-xs text-center text-accent-700 bg-accent-50 py-2 rounded-lg">
                      消息已成功发送，我们会尽快回复您！
                    </p>
                  )}
                  {formState === 'error' && (
                    <p className="text-xs text-center text-accent-700 bg-accent-50 py-2 rounded-lg">
                      {formError || '提交失败，请稍后再试'}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 md:py-12">
          <div className="site-container">
            <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap">
              {[
                { icon: 'ri-shield-check-line', text: '内容安全认证' },
                { icon: 'ri-lock-line', text: '数据加密传输' },
                { icon: 'ri-customer-service-2-line', text: '7x24小时客服' },
                { icon: 'ri-refresh-line', text: '持续更新维护' },
              ].map((badge) => (
                <div key={badge.text} className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className={`${badge.icon} text-secondary-600 text-sm`}></i>
                  </div>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "蚂蚁影视",
            url: "https://www.mantfilm.com",
            description: "蚂蚁影视是国内领先的免费高清在线观影平台，提供电影、电视剧、综艺、动漫等高清免费在线观看服务。",
            foundingDate: "2018",
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "400-888-XXXX",
              contactType: "customer service",
              email: "contact@mantfilm.com",
              availableLanguage: ["Chinese"]
            },
            address: {
              "@type": "PostalAddress",
              addressLocality: "深圳",
              addressRegion: "广东",
              addressCountry: "CN"
            }
          }, null, 2)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://www.mantfilm.com" },
              { "@type": "ListItem", position: 2, name: "关于我们" }
            ]
          }, null, 2)
        }}
      />
    </>
  );
}